import { createFileRoute, Link } from "@tanstack/react-router";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD, softwareApplicationJsonLd } from "@/lib/structured-data";
import { Logo, PageContainer } from "@/components/Logo";
import { MarketingFooter } from "@/components/MarketingShell";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

type QA = { q: string; a: React.ReactNode };
type Section = { id: string; title: string; blurb: string; items: QA[] };

const SECTIONS: Section[] = [
  {
    id: "pricing",
    title: "Pricing & plans",
    blurb: "Trial, paid tiers, billing cycles, and upgrades.",
    items: [
      {
        q: "Is there a free trial? Do I need a credit card?",
        a: (
          <>
            Yes — every paid plan starts with a 30-day trial that unlocks the full feature set, and
            the Free plan stays free with no time limit. No credit card is required to start. You can
            view all plans on the{" "}
            <Link to="/pricing" className="underline underline-offset-2">pricing page</Link>.
          </>
        ),
      },
      {
        q: "What happens when my trial ends?",
        a: "Paid plans include a 30-day trial. If you don't subscribe when it ends, paid features become read-only — your data is preserved and nothing is deleted. Add a payment method any time from Settings → Billing to resume. The Free Forever plan is not a trial and never expires.",
      },
      {
        q: "Can I change plans or cancel later?",
        a: "Yes. Upgrades take effect immediately; downgrades and cancellations take effect at the end of the current billing period. Cancel yourself from Settings → Billing — no phone call required.",
      },
      {
        q: "Do you offer annual billing or non-profit / veteran-owned discounts?",
        a: (
          <>
            Annual billing is available on all paid plans — pay for 10 months, get 12. For veteran-owned,
            first-responder, or volunteer organization pricing, contact us via the{" "}
            <Link to="/contact" className="underline underline-offset-2">contact page</Link>.
          </>
        ),
      },
      {
        q: "How is billing calculated — per user, per site, or flat?",
        a: "Flat. You pay one monthly price for the whole organization — never per user, per report, or per scan. Plans differ only by how many seats and sites they include: Free covers 3 seats and 2 sites, Solo covers 2 seats and 5 sites, Starter 15 seats, Pro 50 seats, and Enterprise unlimited, with unlimited sites from Starter up. Every module is included on every plan; white labeling and API access are the only Enterprise-only items.",
      },
    ],
  },
  {
    id: "onboarding",
    title: "Onboarding & setup",
    blurb: "Getting your team from signup to live patrols.",
    items: [
      {
        q: "How long does it take to get set up?",
        a: "Most teams are running patrols within an hour. Signup, workspace creation, adding your first site, and inviting guards each take a few minutes. Import shortcuts and CSV upload are available for larger rollouts.",
      },
      {
        q: "Can I import existing sites, guards, or checkpoints?",
        a: "Yes. Sites and users support CSV import. Checkpoints can be created individually, generated in bulk, or added by scanning QR codes on the ground during a walkthrough.",
      },
      {
        q: "Do you provide training or a live onboarding call?",
        a: "Self-serve documentation and in-app guides cover the full setup. Paid plans include a scheduled onboarding call on request — reach out through the contact page after subscribing.",
      },
      {
        q: "Can I invite guards without an email address?",
        a: "Yes. You can create guard accounts with a username + PIN for field workers who don't use company email. Admins and supervisors still require email for password recovery and audit accountability.",
      },
    ],
  },
  {
    id: "integrations",
    title: "Integrations",
    blurb: "Connecting MissionReady to the rest of your stack.",
    items: [
      {
        q: "Does MissionReady have a mobile app?",
        a: "Yes. An Android app is in Google Play testing, and MissionReady also installs from the browser on iPhone, Android and desktop as a web app. Both include Voice D.A.R., Radio in Messages, the Hey MR assistant, offline patrol and incident capture, and on-duty GPS. Background location while the screen is off is Android-app only; browser installs report location while the app is open.",
      },
      {
        q: "Does it work offline in the field?",
        a: "Yes. Checkpoint scans, incident reports with photos and video, and Voice DAR memos are all saved on the device when there is no coverage — including through an app reload — and sync themselves the moment the connection returns. Each item is stamped with the time it happened in the field, not the time it uploaded, and can never be filed twice.",
      },
      {
        q: "Do you have an API or webhooks?",
        a: "API tokens are available on the Enterprise plan today, and every organization can export incidents, patrols, time entries, and invoices as CSV or Excel. A public read API and outbound webhooks are on the roadmap — talk to us if you need a specific integration.",
      },
      {
        q: "Do you support AI assistants and agents (MCP)?",
        a: "Yes — MissionReady exposes a Model Context Protocol (MCP) server so tools like Claude, Cursor, and other MCP-compatible clients can securely query your workspace data with your account's exact permissions.",
      },
      {
        q: "Does it integrate with QuickBooks / accounting software?",
        a: "Client invoices and time entries can be exported as CSV for import into QuickBooks and most accounting systems. Direct QuickBooks Online sync is on the roadmap.",
      },
    ],
  },
  {
    id: "security",
    title: "Security & privacy",
    blurb: "How your data is protected and who can see it.",
    items: [
      {
        q: "Where is my data stored?",
        a: "Data is stored in managed Postgres databases with encryption in transit (TLS) and encryption at rest. Uploaded evidence and photos are stored in access-controlled object storage.",
      },
      {
        q: "Can other organizations see my data?",
        a: "No. MissionReady enforces workspace isolation using row-level security at the database level, so every query is scoped to your organization automatically. Cross-organization access is architecturally prevented, not just filtered in application code.",
      },
      {
        q: "How do you handle authentication?",
        a: "Email + password with optional social sign-in (Google). Passwords are hashed, sessions are HTTPS-only, and audit logs record sign-ins and privileged actions. SSO (SAML) is available on request for larger organizations.",
      },
      {
        q: "What about the guard's GPS location — is that always tracked?",
        a: "No. Location is only tracked while an officer is clocked in, and stops at clock-out — never off duty. Geofences decide whether an officer can clock in at a site; they don't pause tracking once on shift. Officers give consent before their first clock-in and see a clear indicator while tracking is active. Full policy is in the Privacy Policy.",
      },
      {
        q: "Are you compliant with SOC 2 / HIPAA / GDPR?",
        a: (
          <>
            We follow security best practices including RLS, encryption, audit logging, and
            least-privilege access. Formal certifications are on our roadmap. For details on data
            processing, see our{" "}
            <Link to="/privacy-policy" className="underline underline-offset-2">Privacy Policy</Link>.
          </>
        ),
      },
      {
        q: "Who owns the data in my workspace?",
        a: "You do. You can export incidents, patrols, and reports at any time. If you cancel, your data remains accessible during the current billing period and is deleted or anonymized shortly after account closure, except where retention is legally required.",
      },
    ],
  },
  {
    id: "support",
    title: "Support",
    blurb: "How to get help — and how fast.",
    items: [
      {
        q: "How do I contact support?",
        a: (
          <>
            Email <a href="mailto:support@missionready.dev" className="underline underline-offset-2">support@missionready.dev</a>,
            use the{" "}
            <Link to="/contact" className="underline underline-offset-2">contact page</Link>, or
            call/text <a href="tel:+15034646006" className="underline underline-offset-2">(503) 464-6006</a>.
            We respond to most inquiries within one business day.
          </>
        ),
      },
      {
        q: "What are your support hours?",
        a: "Standard support is available Monday–Friday, 8am–6pm Pacific. For urgent incidents affecting live field operations, contact the phone line — critical issues are triaged outside business hours.",
      },
      {
        q: "Do you offer priority or dedicated support?",
        a: "Larger accounts can add a dedicated support contact and faster response SLAs. Reach out through the contact page to discuss what your team needs.",
      },
      {
        q: "Where can I report a security issue?",
        a: (
          <>
            Please email security concerns directly through the{" "}
            <Link to="/contact" className="underline underline-offset-2">contact page</Link> with
            "Security" in the subject. We take responsible disclosure seriously and will acknowledge
            reports within one business day.
          </>
        ),
      },
      {
        q: "How do I request a feature?",
        a: "Feature requests are always welcome — send them via the contact page. We prioritize based on field feedback from active operators.",
      },
    ],
  },
];

// Build FAQPage JSON-LD from the sections above so plain-text answers are indexable.
function faqJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: SECTIONS.flatMap((s) =>
      s.items.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: typeof item.a === "string" ? item.a : stripReactToText(item.a),
        },
      }))
    ),
  };
}

function stripReactToText(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(stripReactToText).join("");
  if (typeof node === "object") {
    const maybe = node as unknown as { props?: { children?: React.ReactNode } };
    return stripReactToText(maybe.props?.children);
  }
  return "";
}


export const Route = createFileRoute("/faq")({
  component: FAQPage,
  head: () => ({
    meta: [
      { title: "FAQ — MissionReady" },
      {
        name: "description",
        content:
          "Answers to common questions about MissionReady pricing, onboarding, integrations, security, and support for security operations teams.",
      },
      ...socialMeta({
        title: "MissionReady FAQ — Pricing, Onboarding, Security & Support",
        description: "How pricing works, how fast you can onboard, what integrates, how your data is secured, and how to reach support.",
        url: "https://missionready.dev/faq",
      }),
    ],
    links: [{ rel: "canonical", href: "https://missionready.dev/faq" }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,
      softwareApplicationJsonLd({
        name: "MissionReady",
        description:
          "Answers about MissionReady plans, trials, mobile apps, patrols, dispatch, invoicing, security, and data handling for security operations teams.",
        url: "https://missionready.dev/faq",
      }),
    ],
  }),
});

function FAQPage() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/70 backdrop-blur">
        <PageContainer className="flex h-16 items-center justify-between">
          <Logo />
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm"><Link to="/pricing">Pricing</Link></Button>
            <Button asChild variant="ghost" size="sm"><Link to="/login">Sign in</Link></Button>
            <Button asChild size="sm"><Link to="/signup">Start free trial</Link></Button>
          </div>
        </PageContainer>
      </header>

      <main>
        <PageContainer className="max-w-3xl py-16 md:py-24">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Help center</p>
          <h1 className="mt-3 font-display text-4xl font-bold tracking-tight md:text-5xl">
            Frequently asked questions
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Everything you need to know about pricing, onboarding, integrations, security, and
            support. Can't find your answer?{" "}
            <Link to="/contact" className="text-foreground underline underline-offset-2">
              Get in touch
            </Link>
            .
          </p>

          {/* Quick nav */}
          <nav aria-label="FAQ sections" className="mt-8 flex flex-wrap gap-2">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="rounded-full border border-border bg-card/50 px-3 py-1 text-sm text-muted-foreground hover:border-primary hover:text-foreground"
              >
                {s.title}
              </a>
            ))}
          </nav>

          <div className="mt-12 space-y-14">
            {SECTIONS.map((section) => (
              <section key={section.id} id={section.id} className="scroll-mt-24">
                <h2 className="font-display text-2xl font-semibold tracking-tight">{section.title}</h2>
                <p className="mt-1 text-sm text-muted-foreground">{section.blurb}</p>
                <Accordion type="single" collapsible className="mt-5">
                  {section.items.map((item, i) => (
                    <AccordionItem key={i} value={`${section.id}-${i}`}>
                      <AccordionTrigger className="text-left text-base font-medium">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[15px] leading-relaxed text-muted-foreground">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            ))}
          </div>

          <div className="mt-16 rounded-2xl border border-border bg-card/50 p-6 md:p-8">
            <h2 className="font-display text-2xl font-semibold tracking-tight">Still have questions?</h2>
            <p className="mt-2 text-muted-foreground">
              We answer email within one business day, and the phone line is monitored during
              business hours for urgent field-ops issues.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button asChild><Link to="/contact">Contact us</Link></Button>
              <Button asChild variant="outline"><Link to="/signup">Start free trial</Link></Button>
            </div>
          </div>
        </PageContainer>
      </main>

      <MarketingFooter />



      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd()) }}
      />
    </div>
  );
}
